import React, { useState, useEffect } from 'react';

interface ResearchLoadingStateProps {
  onCancel?: () => void;
}

const PROGRESS_MESSAGES = [
  'Searching the web for market data...',
  'Analyzing competitor insights...',
  'Gathering consumer trends...',
  'Processing market intelligence...',
  'Synthesizing research findings...',
  'Generating comprehensive report...',
];

/**
 * Displays animated loading state during research generation
 * Rotates through progress messages to keep user engaged
 */
export const ResearchLoadingState: React.FC<ResearchLoadingStateProps> = ({ onCancel }) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Rotate messages every 30 seconds
  useEffect(() => {
    const messageTimer = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % PROGRESS_MESSAGES.length);
    }, 30000);

    return () => clearInterval(messageTimer);
  }, []);

  // Track elapsed time
  useEffect(() => {
    const timeTimer = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(timeTimer);
  }, []);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const estimatedMinutes = 3; // Mid-point of 2-4 minute range
  const estimatedSeconds = estimatedMinutes * 60;
  const progress = Math.min((elapsedSeconds / estimatedSeconds) * 100, 95); // Cap at 95% until complete

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <div className="max-w-2xl mx-auto text-center">
        {/* Animated Spinner */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-primary-100 rounded-full"></div>
            <div className="absolute top-0 left-0 w-20 h-20 border-4 border-primary-500 rounded-full border-t-transparent animate-spin"></div>
          </div>
        </div>

        {/* Title */}
        <h3 className="text-2xl font-bold text-gray-900 mb-3">
          Generating Research Report
        </h3>

        {/* Progress Message */}
        <p className="text-lg text-gray-600 mb-6 min-h-[2rem] transition-opacity duration-500">
          {PROGRESS_MESSAGES[messageIndex]}
        </p>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-primary-500 h-2.5 rounded-full transition-all duration-1000 ease-out"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="flex justify-between items-center mt-2 text-sm text-gray-500">
            <span>Elapsed: {formatTime(elapsedSeconds)}</span>
            <span>Estimated: 2-4 minutes</span>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-primary-50 border border-indigo-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-primary-500 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-left">
              <p className="text-sm text-indigo-900 font-medium mb-1">
                Deep research in progress
              </p>
              <p className="text-sm text-primary-700">
                Our AI is conducting comprehensive market research, analyzing web data, and generating detailed insights. This typically takes 2-4 minutes.
              </p>
            </div>
          </div>
        </div>

        {/* What's Happening */}
        <div className="text-left bg-gray-50 rounded-lg p-4 mb-6">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">What we're doing:</h4>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-center">
              <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Searching web for market data
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Analyzing competitor landscape
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Identifying consumer trends
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Generating comprehensive insights
            </li>
          </ul>
        </div>

        {/* Cancel Button (Optional) */}
        {onCancel && (
          <button
            onClick={onCancel}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};
